<template>
  <VDialogV3
    ref="dialog"
    id="store-card"
    position="right"
    width="600px"
    height="100vh"

    :entity="localStore"

    :context-menu="StoresService.contextMenu"
    :on-context-menu-action="onContextMenuAction"
    :submit-method="submit"

    :tabs="tabs"
    v-model:active-tab="activeTab"
  >
    <template #badges>
      <VBadge
        v-if="localStore.isDraft"
        class="draft"
      >
        Черновик
      </VBadge>
    </template>

    <StoreCreateSection
      v-show="activeTab.id === 'main'"
      :local-store="localStore"
    />

    <FilesList
      v-if="activeTab.id === 'files' && localStore.isExisting === true"
      :entities="fileEntities"
    />

    <hr>

    <VHelperText
      v-if="showDraftHint"
      theme="blue-flat"
    >
      <VIcon material="info" theme="outlined" />
      После создания черновик склада отправится на проверку администратора
    </VHelperText>

    <!-- Явный футер: повторяем дефолтные Сохранить/Отмена и добавляем
         админскую кнопку «Утвердить и сохранить» для черновика.
         Сохранение идёт через публичный метод диалога submit(). -->
    <template #footer>
      <VButton
        v-if="canApproveAndSave"
        theme="primary"
        :disabled="isSaveDisabled"
        @click="onApproveAndSubmit"
      >
        Утвердить и сохранить
      </VButton>

      <VButton
        theme="primary"
        :disabled="isSaveDisabled"
        @click="$refs.dialog.submit()"
      >
        Сохранить
      </VButton>

      <VButton @click="closeDialog">
        Отмена
      </VButton>
    </template>
  </VDialogV3>
</template>

<script lang="ts">

import EntityPointer      	from '@/domains/EntityPointer/EntityPointer';
import Store         				from '@/domains/Stores/Store';
import StoresService 				from '@/domains/Stores/StoresService';

import StoreCreateSection 	from '@/domains/Stores/components/StoreCard/StoreCreateSection/StoreCreateSection.vue';

export default defineComponent({
  name: 'StoreCard',

  components: {
    StoreCreateSection,
  },

  props: {
    /** ID склада — передаётся из роутера, карточка сама загружает объект */
    id: {
      type:    Number,
      default: null,
    },

    /** Готовый склад — для редактирования уже загруженного объекта без повторного запроса */
    store: {
      type:    Object as PropType<Store>,
      default: null,
    },
  },

  emits: [
    Store.Events.Created,
    Store.Events.Updated,
    Store.Events.Deleted,
    'before-unmount',
  ],

  setup() {
    return {
      Store,
      StoresService,
    };
  },

  data() {
    const tabs: { id: string, name: string }[] = [
      { id: 'main', name: 'Основное' },
    ];

    return {
      tabs,

      localStore:  undefined as unknown as Store,
      activeTab:   tabs[0],
    };
  },

  async created() {
    await this.init();
  },

  computed: {
    isSaveDisabled(): boolean {
      // нет изменений — нечего сохранять
      return this.$core.utils.isEqual(this.localStore, this.store);
    },

    fileEntities(): EntityPointer[] {
      return [new EntityPointer('store', this.localStore.id, this.localStore.address.stringRepresentation)];
    },

    showDraftHint(): boolean {
      return this.$core.currentUser.isAdmin === false && this.localStore?.isDraft === true;
    },

    canApproveAndSave(): boolean {
      return this.$core.currentUser.isAdmin === true && this.localStore?.isDraft === true;
    },
  },

  beforeUnmount() {
    this.$emit('before-unmount');
  },

  methods: {
    async init(): Promise<void> {
      if (this.$core.utils.isNullOrUndefined(this.id) === false) {
        this.localStore = await StoresService.get(this.id) as Store;
      }
      else if (this.$core.utils.isNullOrUndefined(this.store) === false) {
        this.localStore = this.$core.utils.cloneDeep(this.store);
      }
      else {
        // источник не передан — создание нового склада текущего партнёра
        this.localStore = Store.fromPartner(this.$core.currentPartner);
      }

      // файлы есть только у существующего склада и только у покупателя
      if (this.$core.currentPartner.isBuyer === true && this.localStore.isExisting === true) {
        this.tabs.push({ id: 'files', name: 'Файлы' });
      }
      else {
        // вкладка файлов не нужна
      }
    },

    /**
     * Сохранение склада. Вызывается диалогом через submitMethod: валидацию,
     * блокировку от повторного нажатия и resolve() при успехе делает VDialogV3.
     */
    async submit(): Promise<boolean> {
      if (this.localStore.isExisting === true) {
        const success = await StoresService.update(this.localStore);

        if (success === true) {
          this.$emit(Store.Events.Updated, this.localStore);
        }

        return success;
      }
      else {
        const created = await StoresService.add(this.localStore, this.localStore.partner.id);

        if (created === true) {
          this.$emit(Store.Events.Created, created);
        }

        return created;
      }
    },

    async onApproveAndSubmit(): Promise<void> {
      this.localStore.isDraft = false;
      await this.$refs.dialog.submit();
    },

    /**
     * Контекстное меню выполнило действие (approve / setAsDraft / удаление / редактированиеизвне).
     * По текущему UX закрываем карточку — данные уже отправлены на бек, локальная копия устарела.
     * Транслируем Updated наверх и закрываем через resolve, чтобы реестр релоуднулся.
     */
    onContextMenuAction(): void {
      this.$emit(Store.Events.Updated);
      this.$core.dialogs.StoreCard.resolve();
    },

    closeDialog(): void {
      this.$core.dialogs.StoreCard.hide();
    },
  },
});
</script>